import { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";

const STATUS_TABS = ["Pending", "Approved", "Rejected", "Undo"];
export const ALL_DESK_TYPES = "All";

const canCreateRequestType = (typeLabel) =>
  typeLabel && typeLabel !== "Announcements" && typeLabel !== ALL_DESK_TYPES;

const requestMatchesSearch = (request, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    request.userName,
    request.userEmail,
    request.employeeId,
    request.actionLabel,
  ]
    .filter((value) => value != null && String(value).trim() !== "")
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

const RequestStatusDeskModal = ({
  darkMode = false,
  panelKey,
  panelTypeItems = [],
  selectedType,
  onSelectType,
  selectedStatus,
  onSelectStatus,
  onClose,
  onOpenForm,
  filteredRequests = [],
  selectedRequestId,
  onSelectRequest,
  onOpenStatusAction,
  onOpenBulkStatusAction,
}) => {
  const isApprovalPanel = panelKey === "approvals";
  const showCreateAction = !isApprovalPanel && canCreateRequestType(selectedType);
  const selectedRequest = filteredRequests.find((r) => r.requestId === selectedRequestId);

  const [typeSearchQuery, setTypeSearchQuery] = useState("");
  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);

  const typeItemsWithAll = useMemo(
    () => [ALL_DESK_TYPES, ...panelTypeItems],
    [panelTypeItems]
  );

  const visibleTypeItems = useMemo(() => {
    const q = typeSearchQuery.trim().toLowerCase();
    if (!q) return typeItemsWithAll;
    return typeItemsWithAll.filter((label) => label.toLowerCase().includes(q));
  }, [typeItemsWithAll, typeSearchQuery]);

  const visibleRequests = useMemo(
    () => filteredRequests.filter((request) => requestMatchesSearch(request, itemSearchQuery)),
    [filteredRequests, itemSearchQuery]
  );

  const selectedIdSet = useMemo(() => new Set(selectedRequestIds), [selectedRequestIds]);
  const allVisibleSelected =
    visibleRequests.length > 0 &&
    visibleRequests.every((request) => selectedIdSet.has(request.requestId));

  useEffect(() => {
    setSelectedRequestIds([]);
    setItemSearchQuery("");
  }, [selectedType, selectedStatus, panelKey]);

  useEffect(() => {
    setSelectedRequestIds((prev) =>
      prev.filter((id) => visibleRequests.some((request) => request.requestId === id))
    );
  }, [visibleRequests]);

  const toggleRequestSelection = (requestId) => {
    setSelectedRequestIds((prev) =>
      prev.includes(requestId)
        ? prev.filter((id) => id !== requestId)
        : [...prev, requestId]
    );
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedRequestIds((prev) =>
        prev.filter((id) => !visibleRequests.some((request) => request.requestId === id))
      );
      return;
    }
    const visibleIds = visibleRequests.map((request) => request.requestId);
    setSelectedRequestIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const bulkTargetIds =
    selectedRequestIds.length > 0
      ? selectedRequestIds
      : visibleRequests.map((request) => request.requestId);

  const canBulkApproveReject =
    isApprovalPanel && selectedStatus === "Pending" && bulkTargetIds.length > 0;
  const canBulkUndo =
    !isApprovalPanel && selectedStatus === "Pending" && bulkTargetIds.length > 0;

  const readOnlyInput =
    "mt-1 w-full rounded-md border border-gray-300 bg-gray-50 px-2 py-1.5 text-xs text-black dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:text-gray-100";
  const readOnlyLabel = "text-xs text-gray-700 dark:text-gray-300";

  const renderAppliedFormData = (request) => {
    const fd = request.formData || {};
    if (request.actionLabel === "Leave") {
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className={readOnlyLabel}>
              Leave Type
              <input readOnly value={fd.leaveType || ""} className={readOnlyInput} />
            </label>
            <label className={readOnlyLabel}>
              Duration
              <input readOnly value={fd.leaveDuration || ""} className={readOnlyInput} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className={readOnlyLabel}>
              From Date
              <input readOnly value={fd.fromDate || ""} className={readOnlyInput} />
            </label>
            <label className={readOnlyLabel}>
              To Date
              <input readOnly value={fd.toDate || ""} className={readOnlyInput} />
            </label>
          </div>
        </div>
      );
    }
    if (request.actionLabel === "Resignation") {
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className={readOnlyLabel}>
              Resignation Date
              <input readOnly value={fd.resignationDate || ""} className={readOnlyInput} />
            </label>
            <label className={readOnlyLabel}>
              Last Working Date
              <input readOnly value={fd.lastWorkingDate || ""} className={readOnlyInput} />
            </label>
          </div>
          <label className={readOnlyLabel}>
            Reason Category
            <input readOnly value={fd.resignationReason || ""} className={readOnlyInput} />
          </label>
        </div>
      );
    }
    if (request.actionLabel === "Change Shift") {
      return (
        <div className="grid grid-cols-3 gap-2">
          <label className={readOnlyLabel}>
            Effective Date
            <input readOnly value={fd.date || ""} className={readOnlyInput} />
          </label>
          <label className={readOnlyLabel}>
            Current Shift
            <input readOnly value={fd.currentShift || ""} className={readOnlyInput} />
          </label>
          <label className={readOnlyLabel}>
            New Shift
            <input readOnly value={fd.shift || ""} className={readOnlyInput} />
          </label>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 gap-1 text-[11px] text-gray-700 dark:text-gray-300">
        {Object.entries(fd)
          .filter(([, value]) => String(value || "").trim() !== "")
          .map(([key, value]) => (
            <div key={key} className="flex justify-between gap-3">
              <span className="font-medium text-gray-500 dark:text-gray-400">{key}</span>
              <span className="text-right">{String(value)}</span>
            </div>
          ))}
      </div>
    );
  };

  const actionButtons = isApprovalPanel
    ? [
        { status: "Approved", label: "Approve" },
        { status: "Rejected", label: "Reject" },
        { status: "Undo", label: "Undo" },
      ]
    : [{ status: "Undo", label: "Undo" }];

  return (
    <div className="fixed inset-0 z-[10015] flex items-center justify-center bg-black/45 p-4">
      <div className="flex h-[78vh] w-[92.2rem] max-w-[96vw] overflow-hidden rounded-lg border border-gray-200 bg-white text-black shadow-xl dark:border-[#3C3C4C] dark:bg-[#121212] dark:text-gray-100">
        {/* Left type sidebar — full modal height */}
        <aside className="flex w-[200px] shrink-0 flex-col border-r border-gray-200 dark:border-[#3C3C4C]">
          <div className="space-y-2 border-b border-gray-200 px-2 py-2 dark:border-[#3C3C4C]">
            <div className="px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {isApprovalPanel ? "Approval Types" : "Request Types"}
            </div>
            <div className="relative">
              <Search
                size={13}
                className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="search"
                value={typeSearchQuery}
                onChange={(e) => setTypeSearchQuery(e.target.value)}
                placeholder="Search types..."
                className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-7 pr-2 text-[11px] text-gray-800 placeholder:text-gray-400 dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:text-gray-100"
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-2">
            {visibleTypeItems.map((typeLabel) => {
              const active = selectedType === typeLabel;
              return (
                <button
                  key={typeLabel}
                  type="button"
                  onClick={() => onSelectType(typeLabel)}
                  className={`w-full rounded-md px-2 py-2 text-left text-[11px] leading-snug transition ${
                    active
                      ? "bg-redhalo-red font-semibold text-white"
                      : "text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-[#1a1a1a]"
                  }`}
                >
                  {typeLabel}
                </button>
              );
            })}
            {visibleTypeItems.length === 0 && (
              <div className="px-2 py-3 text-center text-[11px] text-gray-500 dark:text-gray-400">
                No types match your search.
              </div>
            )}
          </div>
        </aside>

        {/* Main content */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-[#3C3C4C]">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {isApprovalPanel ? "Approval Status Desk" : "Request Status Desk"}
              </h3>
              <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                {selectedType === ALL_DESK_TYPES ? "All types" : selectedType || "Request"}
                {isApprovalPanel ? " — items pending your action" : " — requests raised by you"}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-[#3C3C4C] dark:text-gray-200 dark:hover:bg-[#1a1a1a]"
              aria-label="Close status modal"
            >
              <X size={14} />
            </button>
          </div>

          <div className="flex items-end justify-between gap-3 border-b border-gray-200 px-4 dark:border-[#3C3C4C]">
            <div className="flex min-w-0 flex-1 gap-0" role="tablist">
              {STATUS_TABS.map((status) => {
                const active = selectedStatus === status;
                return (
                  <button
                    key={status}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => onSelectStatus(status)}
                    className={`relative shrink-0 px-4 py-2.5 text-xs font-semibold transition ${
                      active
                        ? "text-redhalo-red dark:text-redhalo-mint"
                        : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                    }`}
                  >
                    {status}
                    {active ? (
                      <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-redhalo-red dark:bg-redhalo-mint" />
                    ) : null}
                  </button>
                );
              })}
            </div>
            {showCreateAction ? (
              <button
                type="button"
                onClick={onOpenForm}
                className="mb-2 shrink-0 rounded-md border border-redhalo-red bg-redhalo-red px-3 py-1 text-[11px] font-semibold text-white hover:opacity-95"
              >
                {selectedType}
              </button>
            ) : null}
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-hidden p-4 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="flex min-h-0 flex-col rounded-md border border-gray-200 p-2 dark:border-[#3C3C4C]">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-2 dark:border-[#3C3C4C]">
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  {selectedStatus} Items
                  <span className="ml-1 font-normal text-gray-500">
                    ({visibleRequests.length})
                  </span>
                </div>
              </div>

              <div className="relative mb-2">
                <Search
                  size={13}
                  className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="search"
                  value={itemSearchQuery}
                  onChange={(e) => setItemSearchQuery(e.target.value)}
                  placeholder="Search name, email, employee ID..."
                  className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-7 pr-2 text-[11px] text-gray-800 placeholder:text-gray-400 dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:text-gray-100"
                />
              </div>

              {visibleRequests.length > 0 && (
                <div className="mb-2 flex flex-wrap items-center gap-2 border-b border-gray-100 pb-2 dark:border-[#2a2a2a]">
                  <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                    />
                    Select all
                  </label>
                  {selectedRequestIds.length > 0 ? (
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      {selectedRequestIds.length} selected
                    </span>
                  ) : (
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      Actions apply to visible list
                    </span>
                  )}
                  {canBulkApproveReject && onOpenBulkStatusAction ? (
                    <>
                      <button
                        type="button"
                        onClick={() => onOpenBulkStatusAction(bulkTargetIds, "Approved")}
                        className="rounded border border-green-300 bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-800 hover:bg-green-100 dark:border-green-700 dark:bg-green-900/30 dark:text-green-200"
                      >
                        Approve {selectedRequestIds.length > 0 ? "selected" : "all"}
                      </button>
                      <button
                        type="button"
                        onClick={() => onOpenBulkStatusAction(bulkTargetIds, "Rejected")}
                        className="rounded border border-red-300 bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-800 hover:bg-red-100 dark:border-red-700 dark:bg-red-900/30 dark:text-red-200"
                      >
                        Reject {selectedRequestIds.length > 0 ? "selected" : "all"}
                      </button>
                    </>
                  ) : null}
                  {canBulkUndo && !isApprovalPanel && onOpenBulkStatusAction ? (
                    <button
                      type="button"
                      onClick={() => onOpenBulkStatusAction(bulkTargetIds, "Undo")}
                      className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
                    >
                      Undo {selectedRequestIds.length > 0 ? "selected" : "all"}
                    </button>
                  ) : null}
                </div>
              )}

              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
                {visibleRequests.map((request) => {
                  const isRowSelected = selectedRequestId === request.requestId;
                  const isChecked = selectedIdSet.has(request.requestId);
                  return (
                    <div
                      key={request.requestId}
                      className={`flex gap-2 rounded-md border px-2 py-2 transition ${
                        isRowSelected
                          ? "border-redhalo-red bg-redhalo-mint/35 dark:border-redhalo-mint dark:bg-[#1D1D1D]"
                          : "border-gray-200 bg-gray-50 hover:bg-gray-100 dark:border-[#3C3C4C] dark:bg-[#1a1a1a] dark:hover:bg-[#232323]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleRequestSelection(request.requestId)}
                        className="mt-0.5 rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                        aria-label={`Select ${request.userName || "request"}`}
                      />
                      <button
                        type="button"
                        onClick={() => onSelectRequest(request.requestId)}
                        className="min-w-0 flex-1 text-left"
                      >
                        <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                          {request.actionLabel}
                        </div>
                        <div className="mt-0.5 text-[11px] text-gray-600 dark:text-gray-400">
                          {request.userName}
                          {request.employeeId ? ` (ID: ${request.employeeId})` : ""}
                        </div>
                        {request.userEmail ? (
                          <div className="text-[10px] text-gray-500">{request.userEmail}</div>
                        ) : null}
                        <div className="text-[10px] text-gray-500">
                          {new Date(request.submittedAt).toLocaleString()}
                        </div>
                      </button>
                    </div>
                  );
                })}
                {visibleRequests.length === 0 && (
                  <div className="rounded-md border border-dashed border-gray-300 px-2 py-3 text-center text-xs text-gray-500 dark:border-[#3C3C4C] dark:text-gray-400">
                    {itemSearchQuery.trim()
                      ? "No items match your search."
                      : "No requests found for this status."}
                  </div>
                )}
              </div>
            </div>

            <div className="min-h-0 flex flex-col rounded-md border border-gray-200 p-3 dark:border-[#3C3C4C]">
              {!selectedRequest ? (
                <div className="flex h-full min-h-[240px] items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                  Select a request from the list to view details
                  {showCreateAction ? `, or click "${selectedType}" to create a new request.` : "."}
                </div>
              ) : (
                <div className="flex h-full min-h-0 flex-col">
                  <div className="border-b border-gray-200 pb-2 dark:border-[#3C3C4C]">
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {selectedRequest.actionLabel}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Requested by {selectedRequest.userName}
                      {selectedRequest.userEmail ? ` · ${selectedRequest.userEmail}` : ""}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(selectedRequest.submittedAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="min-h-0 flex-1 space-y-3 overflow-y-auto py-3 pr-1">
                    <div className="rounded-md border border-gray-200 p-2 dark:border-[#3C3C4C]">
                      <div className="mb-1 text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                        Applied Form Data
                      </div>
                      {renderAppliedFormData(selectedRequest)}
                      <div className="mt-2 space-y-2">
                        <label className={readOnlyLabel}>
                          Reason
                          <textarea
                            readOnly
                            rows={2}
                            value={selectedRequest.formData?.reason || ""}
                            className={readOnlyInput}
                          />
                        </label>
                        <label className={readOnlyLabel}>
                          Notes
                          <textarea
                            readOnly
                            rows={2}
                            value={selectedRequest.formData?.notes || ""}
                            className={readOnlyInput}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="rounded-md border border-gray-200 p-2 dark:border-[#3C3C4C]">
                      <div className="mb-1 text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                        Workflow Details
                      </div>
                      {selectedRequest.status === "Pending" ? (
                        <div className="text-xs text-gray-700 dark:text-gray-300">
                          Awaiting approval from:{" "}
                          <span className="font-semibold">
                            {selectedRequest.pendingWith || "Reporting Manager"}
                          </span>
                        </div>
                      ) : (
                        <div className="space-y-1 text-xs">
                          {(selectedRequest.workflowTrail || [])
                            .filter((step) => step.status === selectedRequest.status)
                            .map((step, idx) => (
                              <div
                                key={`${step.actedAt}-${idx}`}
                                className="rounded border border-gray-200 px-2 py-1 dark:border-[#3C3C4C]"
                              >
                                <div className="font-medium text-gray-800 dark:text-gray-100">
                                  {step.status} by {step.actor}
                                </div>
                                {step.reason ? (
                                  <div className="text-[10px] text-gray-600 dark:text-gray-300">
                                    Remarks: {step.reason}
                                  </div>
                                ) : null}
                                <div className="text-[10px] text-gray-500">
                                  {new Date(step.actedAt).toLocaleString()}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-2 dark:border-[#3C3C4C]">
                    {actionButtons
                      .filter(({ status }) =>
                        isApprovalPanel
                          ? true
                          : status === "Undo" && selectedRequest.status === "Pending"
                      )
                      .map(({ status, label }) => (
                        <button
                          key={`${selectedRequest.requestId}-${status}`}
                          type="button"
                          onClick={() => onOpenStatusAction(selectedRequest.requestId, status)}
                          className={`rounded border px-2.5 py-1 text-[11px] font-semibold ${
                            status === "Approved"
                              ? "border-green-300 bg-green-50 text-green-800 hover:bg-green-100 dark:border-green-700 dark:bg-green-900/30 dark:text-green-200"
                              : status === "Rejected"
                              ? "border-red-300 bg-red-50 text-red-800 hover:bg-red-100 dark:border-red-700 dark:bg-red-900/30 dark:text-red-200"
                              : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestStatusDeskModal;
